import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Phone, MessageSquare, Star, MapPin, Navigation } from 'lucide-react';
interface RideTrackingProps {
  onComplete: () => void;
}
export function RideTracking({
  onComplete
}: RideTrackingProps) {
  const [progress, setProgress] = useState(0);
  // Simulate ride progress
  useEffect(() => {
    const timer = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          clearInterval(timer);
          setTimeout(onComplete, 1000);
          return 100;
        }
        return prev + 1;
      });
    }, 50);
    return () => clearInterval(timer);
  }, [onComplete]);
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="relative flex h-full flex-col bg-slate-100" data-id="element-153">
      {/* Abstract Map Background */}
      <div className="absolute inset-0 z-0 overflow-hidden bg-[#e5e7eb]" data-id="element-154">
        {/* Map Grid/Roads */}
        <svg className="h-full w-full opacity-40" viewBox="0 0 390 844" data-id="element-155">
          <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse" data-id="element-156">
            <path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeWidth="2" data-id="element-157" />
          </pattern>
          <rect width="100%" height="100%" fill="url(#grid)" data-id="element-158" />

          {/* Abstract Roads */}
          <path d="M 195 422 L 195 200" stroke="white" strokeWidth="12" strokeLinecap="round" data-id="element-159" />
          <path d="M 50 300 L 340 300" stroke="white" strokeWidth="12" strokeLinecap="round" data-id="element-160" />
          <path d="M 195 422 L 300 600" stroke="white" strokeWidth="12" strokeLinecap="round" data-id="element-161" />

          {/* Route Line */}
          <path d="M 195 422 L 195 300 L 280 300" stroke="#3B82F6" strokeWidth="4" strokeDasharray="8 4" fill="none" className="animate-pulse" data-id="element-162" />
        </svg>

        {/* Map Elements */}
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 transform" data-id="element-163">
          <div className="relative" data-id="element-164">
            <div className="absolute -inset-4 animate-ping rounded-full bg-blue-500/30" data-id="element-165"></div>
            <div className="relative flex h-12 w-12 items-center justify-center rounded-full bg-blue-600 text-white shadow-xl ring-4 ring-white" data-id="element-166">
              <CarIcon className="h-6 w-6" data-id="element-167" />
            </div>
          </div>
        </div>

        <div className="absolute top-1/3 right-1/4 transform" data-id="element-168">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-900 text-white shadow-lg" data-id="element-169">
            <MapPin className="h-4 w-4" data-id="element-170" />
          </div>
        </div>
      </div>

      {/* Driver Card Overlay */}
      <motion.div initial={{
      y: '100%'
    }} animate={{
      y: 0
    }} transition={{
      type: 'spring',
      damping: 25,
      stiffness: 200,
      delay: 0.2
    }} className="absolute bottom-0 left-0 right-0 z-20 rounded-t-[32px] bg-white p-6 shadow-[0_-8px_30px_rgba(0,0,0,0.12)]" data-id="element-171">
        <div className="mx-auto mb-6 h-1 w-12 rounded-full bg-slate-200" data-id="element-172"></div>

        <div className="mb-6 flex items-center justify-between" data-id="element-173">
          <div className="flex items-center gap-4" data-id="element-174">
            <div className="relative" data-id="element-175">
              <img src="https://images.unsplash.com/photo-1633332755192-727a05c4013d?ixlib=rb-4.0.3&auto=format&fit=crop&w=100&q=80" alt="Driver" className="h-14 w-14 rounded-full object-cover ring-2 ring-white shadow-md" data-id="element-176" />
              <div className="absolute -bottom-1 -right-1 flex items-center gap-0.5 rounded-full bg-white px-1.5 py-0.5 text-[10px] font-bold text-slate-900 shadow-sm ring-1 ring-slate-100" data-id="element-177">
                <Star className="h-2.5 w-2.5 fill-yellow-400 text-yellow-400" data-id="element-178" />
                4.9
              </div>
            </div>
            <div data-id="element-179">
              <h3 className="font-bold text-slate-900" data-id="element-180">Michael</h3>
              <p className="text-sm text-slate-500" data-id="element-181">White Toyota Corolla</p>
              <p className="text-xs font-medium text-slate-400" data-id="element-182">ABC 123</p>
            </div>
          </div>
          <div className="text-right" data-id="element-183">
            <p className="text-2xl font-bold text-blue-600" data-id="element-184">
              3 <span className="text-sm font-medium text-slate-500" data-id="element-185">min</span>
            </p>
            <p className="text-xs text-slate-400" data-id="element-186">Arriving</p>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mb-6" data-id="element-187">
          <div className="flex justify-between text-xs font-medium text-slate-500 mb-2" data-id="element-188">
            <span data-id="element-189">On the way</span>
            <span data-id="element-190">Arriving</span>
          </div>
          <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100" data-id="element-191">
            <motion.div className="h-full bg-blue-600" style={{
            width: `${progress}%`
          }} data-id="element-192" />
          </div>
        </div>

        {/* Actions */}
        <div className="grid grid-cols-2 gap-4" data-id="element-193">
          <button className="flex items-center justify-center gap-2 rounded-full bg-blue-50 py-3 text-sm font-bold text-blue-600 transition-colors hover:bg-blue-100" data-id="element-194">
            <Phone className="h-4 w-4" data-id="element-195" />
            Call Driver
          </button>
          <button className="flex items-center justify-center gap-2 rounded-full bg-slate-50 py-3 text-sm font-bold text-slate-600 transition-colors hover:bg-slate-100" data-id="element-196">
            <MessageSquare className="h-4 w-4" data-id="element-197" />
            Message
          </button>
        </div>
      </motion.div>
    </motion.div>;
}
function CarIcon({
  className
}: {
  className?: string;
}) {
  return <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-198">
      <path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.4 2.9A3.7 3.7 0 0 0 2 12v4c0 .6.4 1 1 1h2" data-id="element-199" />
      <circle cx="7" cy="17" r="2" data-id="element-200" />
      <circle cx="17" cy="17" r="2" data-id="element-201" />
      <path d="M5 17h2" data-id="element-202" />
      <path d="M15 17h2" data-id="element-203" />
    </svg>;
}